import { injectable } from 'inversify';
import request, { RequestOptions } from 'graphql-request';
import type { Variables } from 'graphql-request';
import type { GetLogger } from '@unitedtraders/utex-site-common';

import type { GetTokens, GqlConfig, MakeRequestParams, TokensContainer } from './types';

@injectable()
export class GqlService {
  private readonly getTokens: GetTokens;

  private readonly logger: ReturnType<GetLogger>;

  constructor(private config: GqlConfig, sessionService: TokensContainer, getLogger: GetLogger) {
    this.getTokens = sessionService.getTokens;
    this.logger = getLogger('GqlService');
  }

  public makeRequest = <QueryParams extends Variables, QueryResult>({
    requestOptions,
    needAuthorization,
    shouldSetEnvAutomatically
  }: MakeRequestParams<QueryParams, QueryResult>) => {
    const shouldSetEnvAutomaticallyWithFallback = shouldSetEnvAutomatically ?? true;
    const requestVariables = {
      ...(shouldSetEnvAutomaticallyWithFallback && { env: this.config.env }),
      ...requestOptions.variables
    };
    const processedRequestOptions = {
      ...requestOptions,
      ...{ variables: requestVariables }
    } as RequestOptions<QueryParams, QueryResult>;

    if (needAuthorization) {
      return this.getTokens()
        .then(({ accessToken }) => {
          return request({
            url: this.config.gqlGatewayUrl,
            ...processedRequestOptions,
            requestHeaders: {
              ...{ authorization: `Bearer ${accessToken}` },
              ...requestOptions.requestHeaders
            }
          });
        })
        .catch(error => {
          this.logger.error('graphql request error \n', error);
          throw error;
        });
    }

    return request({ url: this.config.gqlGatewayUrl, ...processedRequestOptions }).catch(error => {
      this.logger.error('graphql request error \n', error);
      throw error;
    });
  };
}
